import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Layout } from "@/components/Layout";
import { renderBlocks } from "@/components/Blocks";
import { GUIDES, GUIDES_INDEX } from "@/data/guides";
import { trackEvent } from "@/lib/analytics";

export function GuidesIndex() {
  return (
    <Layout breadcrumbs={GUIDES_INDEX.breadcrumbs}>
      <Seo page={{ path: "/pruvodce", seo: GUIDES_INDEX.seo, h1: GUIDES_INDEX.h1, breadcrumbs: GUIDES_INDEX.breadcrumbs }} />
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <p className="text-xs font-mono font-bold uppercase tracking-[0.25em] text-amber">EDITORIAL</p>
        <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl mt-4">{GUIDES_INDEX.h1}</h1>
        <p className="text-base md:text-lg text-mist mt-4 max-w-2xl">Praktické návody z vlastního provozu: ceny, plánování, vybavení i weather plan. Bez marketingové vaty.</p>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-3" data-testid="guides-index">
        {GUIDES.map((g) => (
          <Link key={g.slug} to={g.path} className="bg-surface p-6 hover:bg-elevated transition-colors group" data-testid={`guide-card-${g.slug}`}>
            <p className="text-[11px] font-mono uppercase tracking-wider text-dim">Aktualizováno {new Date(g.updatedAt).toLocaleDateString("cs-CZ")}</p>
            <h2 className="font-display font-bold uppercase text-white text-xl mt-2 group-hover:text-amber transition-colors">{g.h1}</h2>
            <p className="text-sm text-mist mt-2 leading-relaxed">{g.excerpt}</p>
          </Link>
        ))}
      </div>
    </Layout>
  );
}

export function GuideArticle() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!guide) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-4xl font-extrabold uppercase text-white">Článek nenalezen</h1>
          <Link to="/pruvodce" className="text-amber font-bold uppercase mt-4 inline-block" data-testid="guide-not-found-back">Zpět na průvodce</Link>
        </div>
      </Layout>
    );
  }

  const page = {
    path: guide.path,
    seo: { title: guide.title, description: guide.description, schema: ["Article", "BreadcrumbList"] },
    h1: guide.h1,
    breadcrumbs: guide.breadcrumbs,
  };

  return (
    <Layout breadcrumbs={guide.breadcrumbs}>
      <Seo page={page} guide={guide} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-12" data-testid="guide-article">
        <header>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.25em] text-amber">Průvodce</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.98] text-white text-3xl sm:text-4xl lg:text-5xl mt-4 text-balance">{guide.h1}</h1>
          <p className="text-xs font-mono text-dim mt-5">
            {guide.author} · Publikováno {new Date(guide.publishedAt).toLocaleDateString("cs-CZ")} · Aktualizováno {new Date(guide.updatedAt).toLocaleDateString("cs-CZ")}
          </p>
        </header>
        <div className="mt-10 space-y-10">
          {guide.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-white border-l-2 border-amber pl-4">{s.heading}</h2>
              {s.paras.map((p, i) => (
                <p key={i} className="text-sm sm:text-base text-mist mt-3 leading-relaxed">{p}</p>
              ))}
            </section>
          ))}
        </div>
      </article>
      {renderBlocks(
        [{ t: "cta", heading: guide.cta.heading, text: guide.cta.text }],
        guide.related
      )}
    </Layout>
  );
}
